import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import { format, isToday, isYesterday } from "date-fns";
import { arSA, enUS } from "date-fns/locale";
import { ChevronLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/redux/store";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "../ui/button";
import { ChatIcon } from "../icons";
import ConversationService from "@/firebase/conversation";
import { onSnapshot, Timestamp } from "firebase/firestore";
import ChatWithOwner from "./ChatWithOwner";
import Image from "next/image";
import { ensureFirebaseAuth } from "@/lib/firebase-auth";

interface DialogType {
  createdAt: Timestamp;
  id: string;
  lastMessage: string;
  lastMessageSenderId: string;
  lastMessageTime: Timestamp;
  ownerFullName: string;
  ownerId: string;
  participants: string[];
  propertyId: string;
  propertyImage: string;
  propertyTitle: string;
  userFullName: string;
  userId: string;
}

interface DialogStateType {
  selectedDialog?: DialogType;
  dialogs: DialogType[];
}

const OwnersDialogs = () => {
  const t = useTranslations();
  const locale = useLocale();
  const userData = useAppSelector((state) => state.user);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [open, setOpen] = useState(false);

  const [dialogState, setDialogState] = useState<DialogStateType>({
    selectedDialog: undefined,
    dialogs: [],
  });

  // Set up the current user info from Redux state
  const currentUserId = useMemo(() => userData.id, [userData.id]);

  const firebaseToken = useAppSelector((state) => state.login.firebaseToken);

  const handleSignIn = useCallback(async () => {
    if (!currentUserId) {
      setFirebaseReady(false);
      return;
    }

    try {
      await ensureFirebaseAuth(firebaseToken, currentUserId);
      setFirebaseReady(true);
    } catch (error) {
      setFirebaseReady(false);
      console.error("Error signing in with custom token:", error);
    }
  }, [currentUserId, firebaseToken]);

  useEffect(() => {
    handleSignIn();
  }, [handleSignIn]);

  useEffect(() => {
    if (!currentUserId || !firebaseReady) return;
    const conversationsQuery =
      ConversationService.getConversationQuery(currentUserId);
    const unsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
      const dialogs = (
        snapshot.docs.map((doc) => doc.data()) as DialogType[]
      ).filter((d) => !!d.lastMessageTime);
      setDialogState((prev) => ({ ...prev, dialogs }));
    });
    return () => {
      unsubscribe();
      setDialogState((prev) => ({ ...prev, dialogs: [] }));
    };
  }, [currentUserId, firebaseReady]);

  return (
    <Drawer
      direction={locale === "en" ? "right" : "left"}
      {...{ open, onOpenChange: setOpen }}
    >
      <DrawerTrigger asChild>
        <Button
          variant={"ghost"}
          size={"icon"}
          className="flex outline-none bg-white p-1 rounded-2xl gap-2 indicator"
        >
          {/* {messageState.newMsgs && (
            <span className="indicator-item badge badge-success indicator-start"></span>
          )} */}
          <ChatIcon className="*:!fill-black !size-6" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="!max-w-xl flex-col w-full max-sm:max-w-full">
        <DrawerHeader
          className={cn("border-b-2 mb-2 border-b-[#d9d9d9] w-full", {
            "justify-between items-center flex": dialogState.selectedDialog?.id,
          })}
        >
          <DrawerTitle className="flex-1 !mb-0 text-center">
            {dialogState.selectedDialog?.ownerFullName
              ? t("chatWithOwner", {
                  ownerName: `${dialogState.selectedDialog.ownerFullName} (${dialogState.selectedDialog.propertyTitle})`,
                })
              : t("title")}
          </DrawerTitle>
          {dialogState.selectedDialog?.ownerId ? (
            <Button
              onClick={() => {
                setDialogState((prev) => ({
                  ...prev,
                  selectedDialog: undefined,
                }));
              }}
              className="order-2 min-[463px]:order-3 items-center gap-4"
              variant={"ghost"}
              size={"icon"}
            >
              <ChevronLeft className="ltr:rotate-180" />
            </Button>
          ) : null}
        </DrawerHeader>
        {!dialogState.selectedDialog?.id ? (
          <ul className="flex flex-col overflow-y-auto overflow-x-hidden w-full">
            {!dialogState.dialogs.length ? (
              <div className="flex gap-2 items-center justify-center text-2xl text-muted p-1.5 ">
                {t("noChats")}
              </div>
            ) : (
              dialogState.dialogs.map((d) => (
                <button
                  key={d.id}
                  onClick={() => {
                    setDialogState((prev) => ({
                      ...prev,
                      selectedDialog: d,
                    }));
                  }}
                  className="flex gap-2 items-center border-b-2 transition-all hover:bg-green-main/20 p-1.5 hover:rounded-lg"
                >
                  <div className="border-2 rounded-full p-0.5">
                    <Image
                      src={d.propertyImage}
                      alt={d.propertyTitle}
                      width={56}
                      height={56}
                      className="size-10 sm:size-14 rounded-full"
                    />
                  </div>
                  <div className="flex-1 flex flex-col items-start">
                    <p className="font-semibold">
                      {d.ownerFullName}{" "}
                      <span className="text-muted text-xs">
                        ({d.propertyTitle})
                      </span>
                    </p>
                    <p className="text-muted text-sm">{d.lastMessage}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span>
                      {isToday(d.lastMessageTime!.toDate())
                        ? format(d.lastMessageTime!.toDate(), "hh:mm a", {
                            locale: locale === "en" ? enUS : arSA,
                          })
                        : isYesterday(d.lastMessageTime!.toDate())
                        ? t("yesterday")
                        : format(d.lastMessageTime!.toDate(), "dd LLLL y", {
                            locale: locale === "en" ? enUS : arSA,
                          })}
                    </span>
                    {/* {d.unreadCount ? (
                      <span className="badge badge-success text-white">
                        {d.unreadCount}
                      </span>
                    ) : null} */}
                  </div>
                </button>
              ))
            )}
          </ul>
        ) : (
          <ChatWithOwner selectedDialog={dialogState.selectedDialog} />
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default OwnersDialogs;
